// src/services/api/wallet-transaction.service.ts
import { authService } from '../auth.service';
import { getCurrentApiUrl } from '../../config/server.config';

interface TransferData {
  toAddress: string;
  amount: string;
  feeRate?: number; // Para Bitcoin
}

class WalletTransactionService {
  private get baseUrl(): string {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return getCurrentApiUrl();
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json',
        'Authorization': authService.getAuthorizationHeader(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Request failed: ${response.statusText}`);
    }

    return response.json();
  }

  // Transferir fondos desde una wallet de Ethereum
  async transferEthereum(walletAddress: string, data: TransferData): Promise<any> {
    return this.makeRequest(`/crypto-web3/ethereum/wallet/${walletAddress}/transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Transferir fondos desde una wallet de Bitcoin
  async transferBitcoin(walletAddress: string, data: TransferData): Promise<any> {
    return this.makeRequest(`/crypto-web3/bitcoin/wallet/${walletAddress}/transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Transferir fondos desde una wallet de USDT en Ethereum (ERC-20)
  async transferUSDT(walletAddress: string, data: TransferData): Promise<any> {
    return this.makeRequest(`/crypto-web3/usdt/wallet/${walletAddress}/transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Transferir fondos desde una wallet de Tron
  async transferTron(walletAddress: string, data: TransferData): Promise<any> {
    return this.makeRequest(`/crypto-web3/tron/wallet/${walletAddress}/transfer-trx`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Transferir fondos desde una wallet de BNB
  async transferBNB(walletAddress: string, data: TransferData): Promise<any> {
    return this.makeRequest(`/crypto-web3/bnb/wallet/${walletAddress}/transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Transferir USDT BEP-20
  async transferUSDTBEP20(walletAddress: string, data: TransferData): Promise<any> {
      return this.makeRequest(`/crypto-web3/usdt-bep20/wallet/${walletAddress}/transfer`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
  // Función general para transferir según el tipo de moneda
  async transfer(currency: string, walletAddress: string, transferData: TransferData): Promise<any> {
    if (!currency || !walletAddress) {
      throw new Error('Currency and wallet address are required');
    }

    switch (currency.toUpperCase()) {
      case 'BTC':
        return this.transferBitcoin(walletAddress, transferData);
      case 'ETH':
        return this.transferEthereum(walletAddress, transferData);
      case 'USDT':
      case 'USDT_ERC20':
        return this.transferUSDT(walletAddress, transferData);
      case 'USDC':
      case 'USDC_ERC20':
        return this.makeRequest(`/crypto-web3/usdc/wallet/${walletAddress}/transfer`, {
          method: 'POST',
          body: JSON.stringify(transferData),
        });
      case 'TRX':
        return this.transferTron(walletAddress, transferData);
      case 'USDT_TRON':
      case 'USDT_TRC20':
        return this.makeRequest(`/crypto-web3/tron/wallet/${walletAddress}/transfer-usdt`, {
          method: 'POST',
          body: JSON.stringify(transferData),
        });
      case 'USDC_TRON':
      case 'USDC_TRC20':
        // TRON USDC is not currently supported in backend
        throw new Error('USDC transfers on TRON network are not currently supported');
      case 'BNB':
        return this.transferBNB(walletAddress, transferData);
      case 'USDT_BEP20':
        return this.transferUSDTBEP20(walletAddress, transferData);
      case 'USDC_BEP20':
        // BNB USDC is not currently supported in backend
        throw new Error('USDC transfers on BNB network are not currently supported');
      case 'SOL':
        return this.makeRequest(`/crypto-web3/solana/wallet/${walletAddress}/transfer-sol`, {
          method: 'POST',
          body: JSON.stringify(transferData),
        });
      case 'USDT_SPL':
        return this.makeRequest(`/crypto-web3/solana/wallet/${walletAddress}/transfer-usdt`, {
          method: 'POST',
          body: JSON.stringify(transferData),
        });
      case 'USDC_SPL':
        return this.makeRequest(`/crypto-web3/solana/wallet/${walletAddress}/transfer-usdc`, {
          method: 'POST',
          body: JSON.stringify(transferData),
        });
      default:
        throw new Error(`Unsupported currency for transfer: ${currency}`);
    }
  }

  // Obtener las tarifas actuales para Bitcoin
  async getBitcoinFees(): Promise<any> {
    return this.makeRequest('/crypto-web3/bitcoin/fees');
  }

  // Obtener las tarifas de gas para Ethereum
  async getEthereumGasFees(): Promise<any> {
    return this.makeRequest('/crypto-web3/ethereum/gas-fees');
  }

  // Obtener las tarifas actuales para BNB
  async getBnbFees(): Promise<any> {
    return this.makeRequest('/crypto-web3/bnb/gas-fees');
  }

  // Obtener las tarifas para Tron
  async getTronFees(selectedWallet:string): Promise<any> {
    return this.makeRequest(`/crypto-web3/tron/wallet/${selectedWallet}/fees`);
  }

  // Obtener las tarifas para Solana
  async getSolanaFees(): Promise<any> {
    return this.makeRequest('/crypto-web3/solana/fees');
  }
}

export const walletTransactionService = new WalletTransactionService();